import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { RosterCodeService } from '../services/roster-code.service';
import { IRosterCode } from '../shared/models/roster-code';
import { ToastService } from '../shared/toast/toast.service';
import { DeleteActionComponent } from './delete-action.component';
import { RosterCodeCheckboxComponent } from './roster-code-checkbox.component';

@Component({
  selector: 'pps-manage-roster-code',
  templateUrl: './manage-roster-code.component.html',
  styles: [
  ],
})
export class ManageRosterCodeComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  smallScreen: boolean = false;
  spinnerName: string = "dataSpinner";

  items: MenuItem[] = [
    {label: "Settings", icon: "pi-fw fas fa-tools"}, 
    {label: "Manage Roster Code", icon: "pi-fw far fa-keyboard"},
  ];
  
  rosterCodes: IRosterCode[] = [];
  gridHeaders: any[];
  context:any = {};
  entityLoading: boolean = false;
  gridOptions: any = {
    getRowId: param => param.data.id,
    defaultColDef:{
      enableCellChangeFlash: true,
      resizable: true
    }
  };

  private gridApi;

  constructor(private _rosterCodeService: RosterCodeService,
              private _spinner: NgxSpinnerService,
              private _toast: ToastService,
              private _confirm: ConfirmationService,
              private breakpointObserver: BreakpointObserver) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });       
  }
  
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadData();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }  

  addRosterCode(){
    let rosterCode: IRosterCode = {
      id: 0,
      code: "",
      description: "",
      canBeUsedInTimesheet: false,
      canBeUsedInTimesheetBreakdownRule: false,
      active:true
    };

    this._spinner.show(this.spinnerName);

    this.subs.sink = this._rosterCodeService.add(rosterCode)
      .subscribe(res => {
        if (res.succeed){
          this.rosterCodes.unshift(res.result);
          this.gridApi.setRowData(this.rosterCodes);
          let rowNode = this.gridApi.getRowNode(res.result.id);
          if (rowNode){
            this.gridApi.flashCells({rowNodes: [rowNode]});
            rowNode.setSelected(true);
            window.setTimeout( () =>{
              this.gridApi.startEditingCell({rowIndex: rowNode.rowIndex, colKey: "code"});
            }, 1000);
          }
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  loadData(){
    this._spinner.show(this.spinnerName);
    this.entityLoading = true;
    this.rosterCodes.length = 0;
    this.subs.sink = this._rosterCodeService.getAll()
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.rosterCodes = apiResponse.result;
          this._toast.succeeded(`${this.rosterCodes.length} roster codes loaded`);
        }else{
          this._toast.failed(`Failed to load links: ${apiResponse.errors.join(";")}`);
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  delete(rosterCode: IRosterCode){
    if (rosterCode === null){
      return;
    }

    this._confirm.confirm({
      header: `Delete Roster Code`,
      message: `Are you sure that you want to delete [${rosterCode.code}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteRosterCodeHelper(rosterCode)
    });
  }

  deleteRosterCodeHelper(rosterCode: IRosterCode){
    this.subs.sink = this._rosterCodeService.delete(rosterCode.id)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          var foundIndex = this.rosterCodes.findIndex(x => x.id == rosterCode.id);
          if (foundIndex !== -1 ){
            this.rosterCodes.splice(foundIndex, 1);
            this.gridApi.setRowData(this.rosterCodes);
            this._toast.succeeded(`[${rosterCode.code}] deleted!`);
          }else{
            this._toast.failed(`Delete [${rosterCode.code}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete [${rosterCode.code}]: ${apiResponse.errors.join("|")}`);
        }
      });
  }

  updateRosterCode(rosterCode: IRosterCode){
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._rosterCodeService.update(rosterCode)
      .subscribe(res => {
        if (res.succeed){
          this._toast.succeeded(`Roster code updated`);
        }else{
          this._toast.failed(`Roster code update failed: ${res.errors.join("|")}`);
        }
      })
      .add(() => this._spinner.hide(this.spinnerName));
  }

  private getActionComponentTooltip(rosterCode: IRosterCode):string{
    return "Click to delete roster code: " + rosterCode.code;
  }

  private getColumnDefs(){
    let cols = [
      {
        headerName: "",
        field: "action",
        cellRenderer: DeleteActionComponent,
        resizable: false,
        width: 50,
        hide: false,
        filter: false,        
        suppressMovable: true,
        cellStyle: {textAlign:"center"}
      },
      {
        headerName: 'Code',
        field:'code',
        filter: false,
        width: 100,
        editable: true,
        onCellValueChanged: this.rosterCodeChanged
      },
      {
        headerName: 'Description',
        field:'description',
        filter: false,
        flex: 1,
        editable: true,
        onCellValueChanged: this.rosterCodeChanged
      },
      {
        colId: "can-be-used-in-timesheet",
        headerName: "Can be used in timesheet",
        field: "canBeUsedInTimesheet",
        sortable: true,
        width: 200,
        cellRenderer: RosterCodeCheckboxComponent,
        cellStyle: {textAlign:"center"}
      },
      {
        colId: "can-be-used-in-timesheet-breakdown-rule",
        headerName: "Can be used in payroll breakdown rule",
        field: "canBeUsedInTimesheetBreakdownRule",
        sortable: true,
        width: 260,
        cellRenderer: RosterCodeCheckboxComponent,
        cellStyle: {textAlign:"center"}
      }
    ];

    if (this.smallScreen){
      cols[cols.length-1].flex = 0;
      cols[cols.length-1].width = 500;
    }

    return cols;
  }

  private rosterCodeChanged(params){
    let _this = params.context.componentParent;
    let rosterCode = params.data;
    _this.updateRosterCode(rosterCode);
  }
}
